import { SelectChallengeResultTypenames } from '@varo/graphql-schema-types';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { AddressScreenType } from 'features/auth/verifyAddressChange/constants/addressScreenTypes';
import useSelectChallengeGql from 'features/otp/hooks/useSelectChallengeGql';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';

const ROUTE_PATHS = {
  EMAIL_OTP: '/verify-address-change/enter-email-otp',
  SMS_OTP: '/verify-address-change/enter-sms-otp',
} as const;

const ANALYTICS_KEYS = {
  SETUP_PRESENTED: 'setupPresented',
  EMAIL_SELECTED: 'emailSelected',
  PHONE_SELECTED: 'phoneSelected',
} as const;

type ConfirmIdentitySearchParams = {
  emailChallengeId?: string;
  smsChallengeId?: string;
  screenType?: string;
};

const ConfirmChallengeMethodScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('auth', {
    keyPrefix: 'resetPassword.confirmIdentity',
  });
  const { t: translatedAddressText } = useTranslation('auth', {
    keyPrefix: 'verifyAddressChange.addressScreenTitle',
  });
  const { emailChallengeId, smsChallengeId, screenType } =
    useLocalSearchParams<ConfirmIdentitySearchParams>();
  const { fetching: selectChallengeLoading, selectChallenge } =
    useSelectChallengeGql();

  useViewDidFocusScreenAnalyticsEvent({
    key: ANALYTICS_KEYS.SETUP_PRESENTED,
  });

  const screenTitle =
    screenType &&
    (screenType === AddressScreenType.HOME
      ? translatedAddressText('home')
      : translatedAddressText('mailing'));

  const handleEmailChallenge = useCallback(async () => {
    try {
      const result = await selectChallenge(emailChallengeId ?? '');
      if (
        result.data?.selectChallenge.__typename ===
        SelectChallengeResultTypenames.SelectChallengeError
      ) {
        logBreadcrumb({
          level: 'error',
          message: 'Challenge selection failed',
        });
        return;
      }
      router.navigate({
        pathname: ROUTE_PATHS.EMAIL_OTP,
        params: { challengeId: emailChallengeId, screenType },
      });
    } catch (_error) {
      logBreadcrumb({ level: 'error', message: 'Challenge selection failed' });
    }
  }, [selectChallenge, emailChallengeId, screenType, router]);

  const handleSmsChallenge = useCallback(async () => {
    try {
      const result = await selectChallenge(smsChallengeId ?? '');
      if (
        result.data?.selectChallenge.__typename ===
        SelectChallengeResultTypenames.SelectChallengeError
      ) {
        logBreadcrumb({
          level: 'error',
          message: 'Challenge selection failed',
        });
        return;
      }
      router.navigate({
        pathname: ROUTE_PATHS.SMS_OTP,
        params: { challengeId: smsChallengeId, screenType },
      });
    } catch (_error) {
      logBreadcrumb({ level: 'error', message: 'Challenge selection failed' });
    }
  }, [selectChallenge, smsChallengeId, screenType, router]);

  return (
    <View style={styles.container}>
      <View style={styles.subContainer}>
        <Stack.Screen
          options={{
            title: translatedText('title'),
            headerShown: true,
            headerBackVisible: true,
            header: () => (
              <NavigationBar
                title={screenTitle}
                primaryIcon={<Icon iconKey="BACK" iconSize="MD" />}
                primaryOnPress={navigateBack}
                transparent={false}
              />
            ),
          }}
        />
        <ScreenTransitionOverlay
          isLoading={selectChallengeLoading}
          LoadingComponent={<LoadingSpinner />}
          ErrorComponent={null}
        />

        <View style={styles.dividerContainer}>
          <TitleLgText style={styles.headingContainer} numberOfLines={0}>
            {translatedText('header')}
          </TitleLgText>
          <BodyBaseText style={styles.subHeadingContainer} numberOfLines={0}>
            {translatedText('description')}
          </BodyBaseText>
        </View>

        {emailChallengeId ? (
          <View style={styles.listContainer}>
            <ListItem
              primaryCopy={translatedText('section.email')}
              onPress={() => {
                handleEmailChallenge();
              }}
              analyticsId={ANALYTICS_KEYS.EMAIL_SELECTED}
              customAttributes={{
                challenge_method: translatedText('email'),
              }}
              leadingAccessory={{
                id: '',
                name: VXLComponentId.ICON,
                props: {
                  iconKey: 'EMAIL',
                  iconSize: 'MD',
                },
              }}
            />
          </View>
        ) : null}

        {smsChallengeId ? (
          <>
            <View style={styles.dividerContainer}>
              <HairLineDivider />
            </View>

            <View style={styles.listContainer}>
              <ListItem
                primaryCopy={translatedText('section.message')}
                onPress={() => {
                  handleSmsChallenge();
                }}
                analyticsId={ANALYTICS_KEYS.PHONE_SELECTED}
                customAttributes={{
                  challenge_method: translatedText('phone'),
                }}
                leadingAccessory={{
                  id: '',
                  name: VXLComponentId.ICON,
                  props: {
                    iconKey: 'TEXT',
                    iconSize: 'MD',
                  },
                }}
              />
            </View>
          </>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    display: 'flex',
    flex: 1,
    flexGrow: 1,
    justifyContent: 'flex-start',
    minHeight: `100%`,
    minWidth: `100%`,
  },
  dividerContainer: {
    paddingHorizontal: theme.spacing.m,
  },
  headingContainer: {
    color: theme.color.text.primary,
    paddingVertical: theme.spacing.m,
  },
  listContainer: {
    height: 58,
  },
  subContainer: {
    display: 'flex',
    flex: 1,
    flexGrow: 1,
    justifyContent: 'flex-start',
    width: '100%',
  },
  subHeadingContainer: {
    color: theme.color.text.primary,
    marginBottom: theme.spacing.m,
  },
});

export default ConfirmChallengeMethodScreen;
